// @flow
import * as R from "ramda"
import { createSelector } from "reselect"

import Price from "common/records/Price"
import { isSegmentApplicableMapper } from "common/utils/ancillaryMappers"
import { priceMapper } from "common/utils/priceUtils"
import type { AncillaryService } from "./consts"
import type { AncillaryState, AncillaryStateData } from "./reducer"
import type { AncillaryStandardSegment, CoverMoreSegment, FastTrackSegment } from "./types"

const calculateSegmentPrice = (
  pricePath: Array<string>,
  segment: AncillaryStandardSegment | CoverMoreSegment | FastTrackSegment,
): Price => {
  const price = R.path(pricePath, segment)
  if (!price) {
    console.error(`Can't resolve path "${pricePath.join(".")}" in`, segment)
  }
  if (!isSegmentApplicableMapper(segment)) {
    // $FlowFixMe: error revealed by updating Flow and Ramda libdef, please fix
    return new Price({ amount: 0, currency: R.path(pricePath, segment).currency })
  }
  // $FlowFixMe: error revealed by updating Flow and Ramda libdef, please fix
  return new Price({ ...priceMapper(price) })
}

const calculatePriceForService = (pricePath: Array<string>, forSegment?: number) => (
  serviceData: AncillaryStateData,
): Price => {
  if (Array.isArray(serviceData)) {
    if (typeof forSegment !== "undefined") {
      return calculateSegmentPrice(pricePath, serviceData[forSegment])
    }
    return R.reduce(
      (sumPrice, segment) => Price.sum([sumPrice, calculateSegmentPrice(pricePath, segment)]),
      // $FlowFixMe: error revealed by updating Flow and Ramda libdef, please fix
      new Price({ amount: 0, currency: R.path(pricePath, serviceData[0]).currency }),
      serviceData,
    )
  }
  if (serviceData) {
    return calculateSegmentPrice(pricePath, serviceData)
  }
  return new Price({ amount: 0 })
}

const getAllAncillaryServices = state => R.path(["reservation", "additionalServices"], state)

const getApplicableSegments = (
  segmentsArray: ?(AncillaryStandardSegment[]) | ?(CoverMoreSegment[]) | ?(FastTrackSegment[]),
): $ReadOnlyArray<AncillaryStandardSegment | CoverMoreSegment | FastTrackSegment> =>
  Array.isArray(segmentsArray) ? R.filter(seg => isSegmentApplicableMapper(seg), segmentsArray) : []

const isServiceApplicable = (serviceData: AncillaryStateData): boolean => {
  if (Array.isArray(serviceData)) {
    return getApplicableSegments(serviceData).length > 0
  }
  return Boolean(serviceData && isSegmentApplicableMapper(serviceData))
}

/**
 * Factory for selector that returns state object of the ancillary service defined
 * by serviceName param.
 * @param {string} serviceName - name of the wanted service state
 */
export const serviceStateSelector = (serviceName: string) => (state: any): AncillaryState =>
  R.path(["reservation", "additionalServices", serviceName], state)

/**
 * Factory for selector that returns data object from state of the ancillary service defined
 * by serviceName param.
 * @param {string} serviceName - name of the wanted service state
 */
export const serviceDataSelector: $FlowFixMe = (serviceName: string) =>
  createSelector(
    serviceStateSelector(serviceName),
    R.prop("data"),
  )

/**
 * Factory for selector that returns isAdded of the ancillary service defined by serviceName param.
 * @param {string} serviceName - name of the wanted service state
 */
export const serviceAddedSelector: $FlowFixMe = (serviceName: string) =>
  createSelector(
    serviceStateSelector(serviceName),
    R.prop("isAdded"),
  )

/**
 * Factory for selector that returns forPassengers of the ancillary service defined by
 * serviceStateSelector
 * @param {string} serviceName - name of the wanted service state
 */
export const servicePassengersSelector: $FlowFixMe = (serviceName: string) =>
  createSelector(
    serviceStateSelector(serviceName),
    R.prop("forPassengers"),
  )

/**
 * Factory for selector that returns Price of the ancillary service defined by serviceName
 * @param {string} serviceName - name of the wanted service state
 * @param {Array<string>} pricePath - optional argument for path to price object relative to service data
 * default value is in root of the service data object or its segment
 * @param {number} forSegment - optional argument if you want only one segment price rather than sum
 * If service data is array of segments, sum of all applicable segments is returned
 * If service data is segment object and it's applicable, its price is returned
 * if there is no applicable segments or selected one is not applicable Price of amount 0 is returned
 */
export const servicePriceSelector: $FlowFixMe = (
  serviceName: string,
  pricePath?: Array<string> = ["price"],
  forSegment?: number,
) =>
  createSelector(
    serviceDataSelector(serviceName),
    calculatePriceForService(pricePath, forSegment),
  )

/**
 * Factory for selector that returns if the ancillary service defined by serviceName param is applicable
 * @param {string} serviceName - name of the wanted service state
 * If service data is array of segments, true will be returned if there one or more is_applicable segments
 * If service data is segment object it's is_applicable value is returned
 */
export const serviceApplicableSelector: $FlowFixMe = (serviceName: string) =>
  createSelector(
    serviceDataSelector(serviceName),
    isServiceApplicable,
  )

/**
 * Factory for selector that returns ancillary service applicable segments defined by serviceName param
 * Only for ancillary services with data arrays
 * @param {string} serviceName - name of the wanted service state
 */
export const serviceApplicableSegmentsSelector: $FlowFixMe = (serviceName: string) =>
  createSelector(
    serviceDataSelector(serviceName),
    getApplicableSegments,
  )

/**
 * Returns true if ancillary service is in a loading state
 *
 * @param {string} serviceName - name of the wanted service status
 * @returns {boolean}
 */
export const serviceLoadingSelector: $FlowFixMe = (serviceName: string) =>
  createSelector(
    serviceStateSelector(serviceName),
    service => service.status === "loading" && service.status !== "error",
  )

// TODO
// Put here ancillary services that were refactored to use new Redux structure
// When we refactor all ancillary reducer we don't need this whitelist anymore
const SERVICES_WHITELIST: AncillaryService[] = [
  "airHelp",
  "blueribbonBags",
  "fastTrack",
  "paidCheckin",
]

/**
 * Returns true if all ancillaries loaded
 */
export const allServicesLoadedSelector: $FlowFixMe = createSelector(
  getAllAncillaryServices,
  services => {
    // Filter out services that are not whitelisted
    const serviceNames = Object.keys(services).filter(key => SERVICES_WHITELIST.includes(key))

    // If all services have "valid" status it means that all are loaded
    return serviceNames.every(key => {
      return services[key].status === "valid"
    })
  },
)
